import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, GlobeIcon, LayoutGridIcon, MessageCircleIcon, PencilRulerIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ENGELSKA KLAR™ copy. data/content.ts (MATTE KLAR) is kept untouched and is no longer imported.
// ENGELSKA KLAR™ product mockup (WebP, transparent), hosted in Impultienda Recursos.
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/1b2ecac8-2aca-46db-a73d-351022e4ff30/1792344220020_engelska-klar-mockup-caja-6-volymer-44f731e2.webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '6',
  label: 'Digitala engelskavolymer'
}, {
  value: '12',
  label: 'Lektioner planerade minut för minut'
}, {
  value: '9',
  label: 'Årskurser – från 1 till 9'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '100',
  label: 'Färdiga bedömningskommentarer'
}];
export const differenceStats = [{
  value: 'Årskurs 1–9',
  label: 'Ett system för hela grundskolan'
}, {
  value: '6 digitala volymer',
  label: 'Från planering och speaking till bedömning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut i klassrummet'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas engelsklektioner – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande',
  text: 'Material från olika webbplatser är för lätt, för svårt eller måste anpassas innan det går att använda.'
}, {
  title: 'Allt skapas från grunden',
  text: 'Arbetsblad, ordlistor, samtalsövningar och quiz – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Svårt att få eleverna att prata',
  text: 'Att hitta samtalsövningar och rollspel som faktiskt får hela klassen att använda engelskan tar tid.'
}, {
  title: 'Bedömning och kommentarer tar tid',
  text: 'Prov, matriser och skriftliga kommentarer till varje elev blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa lektion',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop material i sista minuten.'
}];
export const afterItems = ['Ett organiserat engelskasystem för hela grundskolan', 'Årsplanering och lektioner planerade minut för minut', 'Samtalskort och rollspel för speaking', 'Läs- och skrivuppgifter steg för steg', 'Arbetsblad och quiz i grammatik och ordförråd i tre nivåer', 'Kultur, sånger, högtider och projekt från den engelskspråkiga världen', 'Warm-ups och klassrumsspel som skapar variation', 'Prov, bedömningsmatriser och färdiga kommentarer', 'Material som kan skrivas ut direkt'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagarna går åt till planering',
  text: 'Lägger du delar av helgen på att planera engelsklektioner inför kommande vecka?'
}, {
  icon: FolderSearchIcon,
  title: 'Du letar på tio olika webbplatser',
  text: 'Söker du ständigt efter texter, övningar och aktiviteter som passar elevernas nivå?'
}, {
  icon: MessageCircleIcon,
  title: 'Eleverna vågar inte prata engelska',
  text: 'Behöver du fler samtalsövningar och rollspel som får alla elever att använda språket?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, arbetsblad och quiz redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna behöver mer variation',
  text: 'Vill du använda fler spel, sånger, warm-ups och projekt i undervisningen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the MATTE KLAR box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Planering',
  title: 'Årsplanering + 12 lektioner minut för minut',
  tagline: 'Vet exakt vad du ska göra – hela läsåret.',
  intro: 'Den första volymen ger dig en årsplanering för engelska och tolv färdiga lektioner som är planerade minut för minut.',
  items: ['Årsplanering för engelska åk 1–9', '12 färdiga lektioner', 'Upplägg minut för minut', 'Start, genomgång, aktivitet och avslut', 'Lektionsmål', 'Planeringsmallar'],
  outro: 'Öppna, välj lektion och gå in i klassrummet med en tydlig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Speaking',
  title: 'Speaking: samtalskort och rollspel',
  tagline: 'Få eleverna att våga prata engelska.',
  intro: 'Den andra volymen innehåller samtalskort och rollspel som gör det enklare att få igång muntlig engelska i klassrummet.',
  items: ['Samtalskort', 'Rollspel', 'Övningar i par och grupp', 'Vardagliga situationer', 'Utskrivbart material'],
  outro: 'Färdiga övningar som får hela klassen att använda språket.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Läsa & skriva',
  title: 'Läsa & skriva steg för steg',
  tagline: 'Bygg läs- och skrivförmågan steg för steg.',
  intro: 'Den tredje volymen hjälper eleverna att utveckla sin läsförståelse och sitt skrivande på engelska, ett steg i taget.',
  items: ['Läsförståelse', 'Skrivuppgifter', 'Arbete steg för steg', 'Utskrivbara arbetsblad'],
  outro: 'Tydliga uppgifter för läsning och skrivande på engelska.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Grammatik',
  title: 'Grammatik & ordförråd utan tråkighet',
  tagline: 'Grammatik och glosor – utan att det blir tråkigt.',
  intro: 'Den fjärde volymen innehåller arbetsblad och quiz i grammatik och ordförråd i tre nivåer.',
  items: ['Grammatik', 'Ordförråd', 'Arbetsblad', 'Quiz', 'Tre nivåer'],
  outro: 'Ett praktiskt stöd för att möta elever på olika nivåer.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Kultur',
  title: 'Den engelskspråkiga världen',
  tagline: 'Ta in den engelskspråkiga världen i klassrummet.',
  intro: 'Den femte volymen innehåller material om kultur, sånger, högtider och projekt från den engelskspråkiga världen.',
  items: ['Kultur', 'Sånger', 'Högtider', 'Projekt'],
  outro: 'Variation och sammanhang som gör engelskan levande.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Bedömning',
  title: 'Bedömningsstöd',
  tagline: 'Bedöm snabbare och tydligare.',
  intro: 'Den sjätte volymen innehåller prov i två versioner, bedömningsmatriser och 100 färdiga kommentarer.',
  items: ['Prov A och B', 'Bedömningsmatriser', '100 färdiga kommentarer', 'Stöd för uppföljning'],
  outro: 'Ett praktiskt stöd för bedömningsarbetet.',
  note: 'Proven är självständigt framtagna och är inte officiella nationella prov från Skolverket.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för årskurs 1–9',
  text: 'Ett sex-i-ett-system för engelska som hjälper dig att planera och genomföra undervisningen under hela läsåret.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga lektioner',
  text: 'Slipp börja från ett tomt dokument. Tolv lektioner planerade minut för minut hjälper dig att komma igång snabbare.'
}, {
  icon: MessageCircleIcon,
  title: 'Speaking som fungerar',
  text: 'Samtalskort och rollspel som får eleverna att använda engelskan muntligt.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara arbetsblad',
  text: 'Läs-, skriv-, grammatik- och ordförrådsuppgifter som kan skrivas ut och användas direkt.'
}, {
  icon: GlobeIcon,
  title: 'Den engelskspråkiga världen',
  text: 'Kultur, sånger, högtider och projekt som ger engelskan ett sammanhang.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömningsstöd',
  text: 'Prov A/B, bedömningsmatriser och 100 färdiga kommentarer som stöd för bedömning och uppföljning.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Stöd för att organisera engelskundervisningen under hela läsåret.'
}];

// TODO: add real ENGELSKA KLAR™ sample pages. The MATTE KLAR™ math samples (data/content.ts) were not reused,
// so the "Titta inuti" section stays hidden while this list is empty.
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [];
export const pageTypes = ['Färdiga lektioner', 'Samtalskort och rollspel', 'Arbetsblad och quiz', 'Bedömningsstöd'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Engelska på 5 minuter',
  text: '60 korta warm-ups som får igång engelskan i början av lektionen.',
  items: ['60 warm-ups', 'Snabba lektionsstarter', 'Redo att använda direkt'],
  benefit: 'En enkel start på varje lektion.'
}, {
  title: 'Vikariepaketet',
  text: 'Material som gör det enklare att lämna över engelsklektionen till en vikarie.',
  items: ['Färdigt material för vikarien', 'Enkelt att lämna över', 'Utskrivbart'],
  benefit: 'Lugn när du inte själv kan vara på plats.'
}, {
  title: 'Föräldrakommunikation',
  text: 'Stöd för kommunikationen med vårdnadshavare om engelskundervisningen.',
  items: ['Underlag för kontakt med hemmet', 'Färdiga formuleringar'],
  benefit: 'Mindre tid på att skriva till hemmen.'
}, {
  title: 'Engelskabingo & klassrumsspel',
  text: 'Spel som låter eleverna träna engelska på ett engagerande sätt.',
  items: ['Engelskabingo', 'Klassrumsspel', 'Utskrivbart'],
  benefit: 'Mer variation i klassrummet utan att du behöver skapa spelen själv.'
}, {
  title: 'Klassrumsaffischer',
  text: 'Affischer för engelska att sätta upp i klassrummet.',
  items: ['Utskrivbara affischer', 'Visuellt stöd i klassrummet'],
  benefit: 'Ett klassrum som påminner om engelskan varje dag.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna ENGELSKA KLAR™',
  text: 'Ladda ner paketet och öppna volymen du behöver.'
}, {
  title: 'Välj lektion och material',
  text: 'Välj lektion, samtalskort, arbetsblad eller quiz som passar dina elever.'
}, {
  title: 'Skriv ut',
  text: 'Skriv ut det du behöver eller använd materialet digitalt.'
}, {
  title: 'Undervisa',
  text: 'Gå in i klassrummet med en tydlig plan.'
}];
export const audienceItems = ['Undervisar i engelska i årskurs 1–3, 4–6 eller 7–9', 'Vill ha ett helt läsårs engelska organiserat', 'Vill slippa planera lektioner från grunden varje vecka', 'Vill få eleverna att prata mer engelska', 'Vill använda spel, sånger och warm-ups', 'Vill ha enkel tillgång till prov, matriser och kommentarer', 'Vill spara tid på veckoplaneringen', 'Vill känna sig bättre förberedd inför undervisningen'];
export const timeValueTasks = ['Leta efter material på olika webbplatser', 'Planera lektioner', 'Skapa samtalsövningar', 'Göra arbetsblad och quiz', 'Skriva prov och bedömningskommentarer'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer',
  detail: 'Planering, speaking, läsa & skriva, grammatik & ordförråd, kultur och bedömning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Warm-ups, vikariepaket, föräldrakommunikation, spel och affischer'
}, {
  label: 'För årskurs 1–9',
  detail: 'Färdiga lektioner, arbetsblad och bedömningsstöd'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbart material', 'Ingen fysisk produkt skickas'];
export const PRICE = '299 kr';

// Only add authentic reviews of ENGELSKA KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'ENGELSKA KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut det du behöver.'
}, {
  q: 'Är ENGELSKA KLAR™ gjort för den svenska skolan?',
  a: 'Ja. Materialet är framtaget för engelsklärare i den svenska grundskolan, årskurs 1–9. Det är ett fristående undervisningsmaterial och inte en officiell produkt från Skolverket.'
}, {
  q: 'Passar det för årskurs 1–9?',
  a: 'Ja. Du väljer material utifrån den årskurs och det område du undervisar i.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'ENGELSKA KLAR™ är utformat för att göra lektionsförberedelserna snabbare. Du kan öppna en volym, välja en färdig lektion eller ett arbetsblad och gå igenom det på ungefär fem minuter. Tiden kan variera beroende på lektionsinnehåll, elevgrupp och behov av anpassning.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'ENGELSKA KLAR™ innehåller en årsplanering och material som kan användas under hela läsåret. Du väljer, kombinerar och anpassar resurserna efter din planering och elevernas behov.'
}, {
  q: 'Finns det färdiga arbetsblad?',
  a: 'Ja. Materialet innehåller utskrivbara arbetsblad och quiz i grammatik och ordförråd, samt läs- och skrivuppgifter.'
}, {
  q: 'Ingår bedömningsstöd?',
  a: 'Ja. Paketet innehåller prov A/B, bedömningsmatriser och 100 färdiga kommentarer.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. Arbetsbladen och quizen i grammatik och ordförråd finns i tre nivåer, och du kan välja och anpassa materialet utifrån elevernas kunskaper och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. ENGELSKA KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
